import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { api, setCsrfToken } from '../lib/api.js';
import { ThemeToggle } from '../components/ThemeToggle.js';
import { CommandPalette } from '../components/CommandPalette.js';

/**
 * The single point of responsive truth (docs/IMPLEMENTATION-PLAN.md §2):
 * phone gets the bottom tab bar in the thumb zone plus a compact top header;
 * ≥1025px swaps both for a full left sidebar. Screens render into the outlet
 * and never ask about form factor.
 *
 * The sidebar is a real SaaS rail: brand, the four pillars, the secondary
 * destinations, and a footer that is ALWAYS reachable — appearance and
 * sign-out live there, not buried in a scrolling content row. On phones the
 * same controls sit in the top header, which scrolls away because the thumb
 * zone belongs to navigation, not to sign-out.
 */

/** 20px stroke icons — glyph fonts and unicode shapes render differently on
 * every platform; inline SVG is the same product everywhere. */
const ICONS: Record<string, React.ReactNode> = {
  home: (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 9.5 10 3l7 6.5" />
      <path d="M5 8.5V17h10V8.5" />
    </svg>
  ),
  wealth: (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 14l4-4 3 3 7-7" />
      <path d="M13 6h4v4" />
    </svg>
  ),
  goals: (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="3" />
    </svg>
  ),
  insights: (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <path d="M4 16V9" />
      <path d="M10 16V4" />
      <path d="M16 16v-5" />
    </svg>
  ),
};

const TABS = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/wealth', label: 'Wealth', icon: 'wealth' },
  { to: '/goals', label: 'Goals', icon: 'goals' },
  { to: '/insights', label: 'Insights', icon: 'insights' },
];

/**
 * Secondary destinations — NOT pillars (PRODUCT-ARCHITECTURE.md §2.2 fixes the
 * primary navigation at four plus the reserved Money slot). They live in the
 * sidebar's own section on desktop and in the top header row on phones; both
 * keep them one tap from anywhere, so Navigation Law 2 holds.
 */
const UTILITY = [
  { to: '/sips', label: 'SIPs' },
  { to: '/epf', label: 'EPF' },
  { to: '/tax', label: 'Tax' },
  { to: '/family', label: 'Family' },
  { to: '/mailboxes', label: 'Mailboxes' },
  { to: '/activity', label: 'Activity' },
];

/**
 * Sign out is a full server round-trip (the session cookie is HttpOnly), then
 * a hard reload: the App shell re-checks /auth/session, finds none, and lands
 * on Login. No client-side state is trusted to know it is signed out.
 */
async function signOut() {
  try {
    await api.post('/auth/logout');
  } finally {
    setCsrfToken(null);
    window.location.assign('/');
  }
}

function SignOutButton() {
  return (
    <button type="button" className="signout" onClick={() => void signOut()} title="Sign out">
      <span aria-hidden>⎋</span> Sign out
    </button>
  );
}

export function NavShell() {
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl-K anywhere; the button is the same verb for pointer users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const searchButton = (
    <button type="button" className="searchbtn" onClick={() => setPaletteOpen(true)}>
      <span aria-hidden>⌕</span> Find a fund <kbd className="only-desktop">⌘K</kbd>
    </button>
  );

  const utilityLinks = UTILITY.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) => `utility-link${isActive ? ' utility-active' : ''}`}
    >
      {item.label}
    </NavLink>
  ));

  return (
    <div className="shell">
      <nav className="tabbar" aria-label="Primary">
        <div className="brand only-desktop" aria-hidden>
          ApnaKosh
        </div>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/'}
            className={({ isActive }) => `tab${isActive ? ' tab-active' : ''}`}
          >
            <span aria-hidden="true" className="tab-glyph">
              {ICONS[tab.icon]}
            </span>
            {tab.label}
          </NavLink>
        ))}

        {/* Desktop-only sidebar sections; phones carry these in the header. */}
        <div className="nav-section only-desktop" aria-label="More">
          {utilityLinks}
        </div>
        <div className="nav-foot only-desktop">
          {searchButton}
          <ThemeToggle />
          <SignOutButton />
        </div>
      </nav>

      <main className="shell-content">
        <header className="topbar only-mobile">
          <nav className="utility" aria-label="Secondary">
            {utilityLinks}
          </nav>
          <div className="topbar-actions">
            <ThemeToggle />
            {searchButton}
            <SignOutButton />
          </div>
        </header>
        <Outlet />
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
